import type { GeocodingResult, LatLong } from './types';

/** @see https://developers.google.com/maps/documentation/places/web-service/supported_types */

const LOCALITY = 'locality';
const SUBLOCALITY = 'sublocality';
const ADMIN_1 = 'administrative_area_level_1';
const ADMIN_3 = 'administrative_area_level_3';
const NEIGHBORHOOD = 'neighborhood';
const POSTAL_CODE = 'postal_code';
const STREET_NUMBER = 'street_number';
const STREET_ADDRESS_2 = 'subpremise';
const ROUTE = 'route';
const COUNTRY = 'country';

type GeocoderAddressComponent = google.maps.GeocoderAddressComponent;

type GeocoderResult = google.maps.GeocoderResult;

type PlaceTypeKeys =
  | typeof LOCALITY
  | typeof SUBLOCALITY
  | typeof ADMIN_1
  | typeof ADMIN_3
  | typeof NEIGHBORHOOD
  | typeof POSTAL_CODE
  | typeof STREET_NUMBER
  | typeof STREET_ADDRESS_2
  | typeof ROUTE;

type PlaceTypes = {
  [key in PlaceTypeKeys]?: GeocoderAddressComponent;
};

type GenericPlaceType = {
  [key: string]: GeocoderAddressComponent;
};

type CityPlaces = Pick<
  PlaceTypes,
  typeof LOCALITY | typeof SUBLOCALITY | typeof ADMIN_3 | typeof NEIGHBORHOOD
>;

const intersection = (arr1: Array<string>, arr2: Array<string>) => {
  return arr1.filter((item) => arr2.indexOf(item) > -1);
};

export function formatGeocodeResponse(result: GeocoderResult): GeocodingResult {
  const {
    address_components,
    formatted_address,
    geometry: { location },
    place_id: google_place_id,
  } = result;

  const countryCode = countryOf(address_components);
  const city = cityOf(address_components);
  const state = stateOf(address_components);
  const cityState = city && state ? `${city}, ${state}` : 'Unknown';

  const zipcode = zipcodeOf(address_components);
  const street = streetOf(address_components);
  const street2 = street2Of(address_components);
  const coordinates: LatLong = {
    lat: location.lat(),
    long: location.lng(),
  };

  return {
    city,
    cityState,
    coordinates,
    countryCode,
    formattedAddress: formatted_address,
    googlePlaceId: google_place_id,
    state,
    street,
    street2,
    zipcode,
  };
}

export function getPreferredCityComponent(cityContenders: CityPlaces) {
  const locality = cityContenders[LOCALITY];
  const sublocality = cityContenders[SUBLOCALITY];
  const admin3 = cityContenders[ADMIN_3];
  const neighborhood = cityContenders[NEIGHBORHOOD];

  // Prefer placeTypes in this order: locality, sublocality, administrative_area_level_3, neighborhood
  return locality || sublocality || admin3 || neighborhood;
}

export function countryOf(addressComponents: GeocoderAddressComponent[]) {
  const countryComponent = addressComponents.find((component) =>
    component.types.includes(COUNTRY)
  );

  return countryComponent && countryComponent['short_name'];
}

export function cityOf(addressComponents: GeocoderAddressComponent[]) {
  const acceptedCityTypes = [LOCALITY, SUBLOCALITY, ADMIN_3, NEIGHBORHOOD];

  const cityContenders = addressComponents.reduce(
    (acc: GenericPlaceType, addressComponent) => {
      const { types } = addressComponent;
      const cityTypes = intersection(acceptedCityTypes, types);
      if (!cityTypes.length) return acc;
      cityTypes.forEach((cityType) => {
        acc[cityType] = addressComponent;
      });

      return acc;
    },
    {}
  );
  const cityComponent = getPreferredCityComponent(cityContenders);
  return cityComponent?.long_name;
}

export function stateOf(addressComponents: GeocoderAddressComponent[]) {
  const stateComponent = addressComponents.find((component) =>
    component.types.includes(ADMIN_1)
  );

  return stateComponent && stateComponent['short_name'];
}

export function zipcodeOf(addressComponents: GeocoderAddressComponent[]) {
  const zipcodeComponent = addressComponents.find((component) =>
    component.types.includes(POSTAL_CODE)
  );
  return zipcodeComponent && zipcodeComponent.short_name;
}

export function streetOf(addressComponents: GeocoderAddressComponent[]) {
  let street_number: string | undefined, street: string | undefined;

  addressComponents
    .filter(
      (component) =>
        component.types.includes(STREET_NUMBER) ||
        component.types.includes(ROUTE)
    )
    .forEach((component) => {
      if (component.types.includes(STREET_NUMBER)) {
        street_number = component.short_name;
      } else {
        street = component.short_name;
      }
    });

  return street && street_number ? `${street_number} ${street}` : street;
}

export function street2Of(addressComponents: GeocoderAddressComponent[]) {
  const subpremise = addressComponents.find(({ types }) =>
    types.includes(STREET_ADDRESS_2)
  )?.short_name;

  return subpremise ? `#${subpremise}` : undefined;
}
